import React, { useRef, useState, useEffect } from "react";
import { logoutUser, deleteUserAccount, auth } from "../config/firebase";
import { deleteAllUserData } from "../config/firestore";
import CGUScreen from "./CGUScreen";

const C = {
  bg:        "#C4622D",
  header:    "#2D5A3D",
  text:      "#1C1C1E",
  muted:     "#8A8A8E",
  terracotta:"#C4622D",
};

const RESOURCES = [
  { id: "eau",      label: "Eau",      unit: "L",  color: "#378ADD" },
  { id: "gaz",      label: "Gaz",      unit: "%",  color: "#EF9F27" },
  { id: "batterie", label: "Batterie", unit: "%",  color: "#1D9E75" },
];

function getConseilAuto(autonomie, t = {}) {
  const { eau = 70, gaz = 60, batterie = 80, eauxUsees = 20 } = autonomie || {};
  if (eauxUsees > 85) return { text: t.conseilEauxUsees   || "Les eaux usées sont presque pleines — videz-les en priorité.", color: "#C4622D" };
  if (eau < 20)       return { text: t.conseilEauBasse     || "Réservoir d'eau critique — trouvez un point d'eau.", color: "#C4622D" };
  if (gaz < 15)       return { text: t.conseilGazBas       || "Bouteille de gaz presque vide — rechargez bientôt.", color: "#C4622D" };
  if (batterie < 20)  return { text: t.conseilBatterieBasse || "Batterie faible — branchez ou exposez les panneaux.", color: "#C4622D" };
  if (eau < 40 || gaz < 30 || batterie < 40 || eauxUsees > 70)
    return { text: t.conseilSurveiller || "Quelques ressources à surveiller avant le départ.", color: "#D97706" };
  return { text: t.conseilOk || "Autonomie correcte — partez l'esprit tranquille.", color: "#2D9E4A" };
}

const DOC_ORDER = [
  { id: "assurance", label: "Assurance" },
  { id: "garage",    label: "Révision garage" },
  { id: "ct",        label: "Contrôle Technique" },
];

const getConseils = (t) => [
  {
    num: "1",
    titre: t.conseilTitre1 || "La Stat de Poids — PTAC",
    texte: t.conseilTexte1 || "La plupart des camping-cars se conduisent avec un permis B et sont limités à 3,5 tonnes. La Charge Utile est la différence entre le poids à vide et le PTAC (souvent 400 à 600 kg). Calculez : Passagers + plein d'eau (100L = 100kg) + gaz + effets personnels + vélos.\n\n💬 Conseil : Ne partez jamais avec le réservoir d'eau plein à 100% si vous avez de la route.",
  },
  {
    num: "2",
    titre: t.conseilTitre2 || "Autonomie et Ressources",
    texte: t.conseilTexte2 || "• Eau : ~20 litres/jour/personne. Avec 100L, vous tenez 2 à 3 jours à deux.\n• Énergie : Ne descendez pas sous 12,2V (50% de charge) pour ne pas abîmer la batterie.\n• Gaz : Une bouteille de 13kg dure plusieurs semaines en été, mais 3 à 4 jours en hiver avec chauffage intensif.",
  },
  {
    num: "3",
    titre: t.conseilTitre3 || "Dimensions et Navigation",
    texte: t.conseilTexte3 || "• Hauteur : Entre 2,80m et 3,10m. Notez-la sur un post-it sur votre tableau de bord.\n• Porte-à-faux : En virage serré, l'arrière balaye l'extérieur. Gardez toujours une marge de manœuvre.",
  },
  {
    num: "4",
    titre: t.conseilTitre4 || "Organisation Numérique",
    texte: t.conseilTexte4 || "• Apps de stationnement pour trouver des aires de services ou spots autorisés.\n• Check-lists de départ : fermer les bouteilles de gaz, rentrer le marchepied, verrouiller les placards, fermer les lanterneaux.",
  },
  {
    num: "5",
    titre: t.conseilTitre5 || "Le petit plus Sérénité",
    texte: t.conseilTexte5 || "Emportez une petite boîte à outils (fusibles, ampoules, colliers de serrage). Un camping-car subit beaucoup de vibrations — il n'est pas rare de devoir resserrer une vis ou changer un fusible de pompe à eau en route.",
  },
  {
    num: "6",
    titre: t.conseilTitre6 || "Animaux de compagnie",
    texte: t.conseilTexte6 || "Avant de partir\n• Administratif : Animal identifié (puce/tatouage), vaccins à jour (rage obligatoire pour l'étranger) et passeport européen si vous passez les frontières.\n• Santé : Prévoir une trousse de secours (tire-tique, désinfectant) et anticiper le mal des transports.\n\nSur la route — Sécurité\n• Attache obligatoire : Harnais de sécurité ou caisse de transport fixée (jamais d'animal en liberté pendant que vous roulez).\n• Pauses : S'arrêter toutes les deux heures pour l'eau, les besoins et le dégourdir.",
  },
];

function todayFR() {
  return new Date().toLocaleDateString("fr-FR", {
    weekday: "long", day: "numeric", month: "long",
  });
}

function getRappels(dossiers, depenses) {
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const getRappelDate = (dateStr, label) => {
    if (!dateStr) return { label, text: "Non renseigné", color: "#8A8A8E" };
    const date = new Date(dateStr);
    const days = Math.round((date - today) / 86400000);
    let color;
    if (days < 7)       color = "#C4622D";
    else if (days < 30) color = "#EA7B20";
    else                color = "#2D9E4A";
    const text = days < 0 ? "Expiré" : days === 0 ? "Aujourd'hui" : `dans ${days} j`;
    return { label, text, color };
  };

  const kmRevision = dossiers?.garage?.kmRevision;
  const kms = (depenses || []).filter(d => d.type === "carburant").map(d => Number(d.km)).filter(k => !isNaN(k) && k > 0);
  const kmActuel = kms.length ? Math.max(...kms) : null;
  let garageRappel;
  if (!kmRevision) {
    garageRappel = { label: "Révision", text: "Non renseigné", color: "#8A8A8E" };
  } else {
    const diff = Number(kmRevision) - (kmActuel || 0);
    if (diff <= 0) {
      garageRappel = { label: "Révision", text: "Révision due", color: "#C4622D" };
    } else if (diff <= 2000) {
      garageRappel = { label: "Révision", text: `${diff.toLocaleString("fr-FR")} km`, color: "#EA7B20" };
    } else {
      garageRappel = { label: "Révision", text: `${diff.toLocaleString("fr-FR")} km`, color: "#2D9E4A" };
    }
  }

  return [
    getRappelDate(dossiers?.assurance?.date, "Assurance"),
    garageRappel,
    getRappelDate(dossiers?.ct?.date, "CT"),
  ];
}

function ScoreRing({ score }) {
  const r    = 38;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <svg width={96} height={96} viewBox="0 0 96 96">
      <circle cx={48} cy={48} r={r} fill="none" stroke="rgba(45,90,61,0.12)" strokeWidth={9} />
      <circle
        cx={48} cy={48} r={r} fill="none" stroke={C.header} strokeWidth={9}
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap="round"
        transform="rotate(-90 48 48)"
        style={{ transition: "stroke-dasharray 0.6s ease" }}
      />
      <text x={48} y={46} textAnchor="middle" dominantBaseline="middle"
        fontSize={22} fontWeight={900} fill={C.header}>{score}</text>
      <text x={48} y={62} textAnchor="middle" fontSize={8} fill={C.muted} fontWeight={600}>SCORE</text>
    </svg>
  );
}

function VanSVG() {
  return (
    <svg width="120" height="70" viewBox="0 0 140 80" fill="none">
      <rect x="6"   y="22" width="128" height="42" rx="8" fill="#2D5A3D" />
      <rect x="20"  y="10" width="90"  height="20" rx="8" fill="#3A6B4A" />
      <rect x="22"  y="13" width="26"  height="14" rx="3" fill="#A8D5C2" opacity="0.85" />
      <rect x="54"  y="13" width="26"  height="14" rx="3" fill="#A8D5C2" opacity="0.85" />
      <rect x="86"  y="13" width="22"  height="14" rx="3" fill="#A8D5C2" opacity="0.85" />
      <rect x="54"  y="26" width="42"  height="30" rx="4" fill="#346348" />
      <circle cx="94" cy="41" r="2.5" fill="#9FE1CB" />
      <rect x="8"   y="34" width="10"  height="7"  rx="3" fill="#F8F6F1" opacity="0.9" />
      <rect x="122" y="34" width="8"   height="7"  rx="3" fill="#C45A3A" opacity="0.85" />
      <rect x="6"   y="56" width="128" height="8"        fill="#1C3A28" />
      <circle cx="30"  cy="66" r="13" fill="#1A1A1A" />
      <circle cx="30"  cy="66" r="8"  fill="#2A2A2A" />
      <circle cx="30"  cy="66" r="4"  fill="#3D3D3D" />
      <circle cx="30"  cy="66" r="1.5" fill="#9FE1CB" />
      <circle cx="108" cy="66" r="13" fill="#1A1A1A" />
      <circle cx="108" cy="66" r="8"  fill="#2A2A2A" />
      <circle cx="108" cy="66" r="4"  fill="#3D3D3D" />
      <circle cx="108" cy="66" r="1.5" fill="#9FE1CB" />
    </svg>
  );
}

function FadeBtn({ onClick, style, children }) {
  const [op, setOp] = useState(0.4);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setOp(1)}
      onMouseLeave={() => setOp(0.4)}
      onMouseDown={() => setOp(1)}
      onTouchStart={() => setOp(1)}
      onTouchEnd={() => setOp(0.4)}
      style={{ ...style, opacity: op, transition: "opacity 0.2s" }}
    >
      {children}
    </button>
  );
}

export default function AccueilScreen({
  van, score, setTab, onPhotoChange, onPhotoDelete, onVanUpdate,
  dossiers, lastCheckDate, autonomie, checks, onSOS, userEmail, onShowPremium, depenses, t = {}, lang = "fr", setLang,
}) {
  const fileRef = useRef(null);
  const rappels = getRappels(dossiers, depenses);
  const conseils = getConseils(t);

  const [showConfirm,          setShowConfirm]          = useState(false);
  const [showEdit,             setShowEdit]             = useState(false);
  const [showInfo,             setShowInfo]             = useState(false);
  const [showCGU,              setShowCGU]              = useState(false);
  const [showConseils,         setShowConseils]         = useState(false);
  const [weather,              setWeather]              = useState(null);
  const [weatherErr,           setWeatherErr]           = useState(null);
  const [confirmDeleteAccount, setConfirmDeleteAccount] = useState(false);
  const [deletingAccount,      setDeletingAccount]      = useState(false);

  useEffect(() => {
    if (!confirmDeleteAccount) return;
    const timer = setTimeout(() => setConfirmDeleteAccount(false), 5000);
    return () => clearTimeout(timer);
  }, [confirmDeleteAccount]);

  const handleDeleteAccount = async () => {
    if (!confirmDeleteAccount) { setConfirmDeleteAccount(true); return; }
    setConfirmDeleteAccount(false);
    setDeletingAccount(true);
    try {
      const uid = auth.currentUser?.uid;
      await deleteAllUserData(uid);
      await deleteUserAccount();
      await logoutUser();
    } catch (e) {
      setDeletingAccount(false);
      if (e.code === "auth/requires-recent-login") {
        alert(t.erreurReconnexion || "Veuillez vous déconnecter et vous reconnecter avant de supprimer le compte.");
      } else {
        alert((t.erreurSuppression || "Erreur : ") + e.message);
      }
    }
  };

  useEffect(() => {
    const KEY = process.env.REACT_APP_WEATHER_API_KEY;

    const iconMap = {
      "01": "☀️", "02": "⛅", "03": "☁️", "04": "☁️",
      "09": "🌧️", "10": "🌦️", "11": "⛈️", "13": "❄️", "50": "🌫️",
    };
    const toEmoji = (icon) => iconMap[(icon || "01").slice(0, 2)] || "🌤️";

    const fetchWeather = async (lat, lon, usingDefault) => {
      if (!KEY) {
        setWeather({ temp: "—", emoji: "🌤️", desc: "Clé API manquante", city: "Paris", days: [], usingDefault: true });
        return;
      }
      try {
        const res  = await fetch(
          `https://api.openweathermap.org/data/2.5/forecast?lat=${lat}&lon=${lon}&appid=${KEY}&units=metric&lang=fr`
        );
        const data = await res.json();
        if (String(data.cod) !== "200") { setWeatherErr("api"); return; }
        const current = data.list[0];
        const days = [];
        const seen = new Set();
        for (const item of data.list) {
          const day = item.dt_txt.slice(0, 10);
          if (!seen.has(day)) { seen.add(day); days.push(toEmoji(item.weather[0].icon)); }
          if (days.length >= 7) break;
        }
        setWeather({
          temp: Math.round(current.main.temp),
          emoji: toEmoji(current.weather[0].icon),
          desc: current.weather[0].description,
          city: data.city.name,
          days,
          usingDefault,
        });
      } catch { setWeatherErr("api"); }
    };

    const fallback = () => fetchWeather(48.8566, 2.3522, true);

    if (!navigator.geolocation) { fallback(); return; }

    let done = false;
    const timer = setTimeout(() => { if (!done) { done = true; fallback(); } }, 5000);

    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude: lat, longitude: lon } }) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        fetchWeather(lat, lon, false);
      },
      () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        fallback();
      },
      { timeout: 4000, maximumAge: 60000 }
    );
  }, []);
  const [editNom,     setEditNom]     = useState("");
  const [editImmat,   setEditImmat]   = useState("");

  const handleFile = e => {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = ev => onPhotoChange(ev.target.result);
    reader.readAsDataURL(f);
  };

  const openEdit = () => {
    setEditNom(van?.nom || "");
    setEditImmat(van?.immat || "");
    setShowEdit(true);
  };

  return (
    <div style={{ background: C.bg, minHeight: "100%", paddingBottom: 24 }}>

      {/* Popup — supprimer photo */}
      {showConfirm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500, padding: "0 24px" }}>
          <div style={{ background: "white", borderRadius: 18, padding: "28px 22px", width: "100%", maxWidth: 340, boxShadow: "0 8px 40px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: C.text, marginBottom: 8 }}>Supprimer la photo ?</div>
            <div style={{ fontSize: 14, color: C.muted, marginBottom: 26, lineHeight: 1.5 }}>Voulez-vous vraiment supprimer la photo ?</div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowConfirm(false)} style={{ flex: 1, padding: "12px 0", borderRadius: 11, border: "1.5px solid #D3D1C7", background: "white", fontSize: 14, fontWeight: 700, color: C.text, cursor: "pointer", fontFamily: "inherit" }}>Annuler</button>
              <button onClick={() => { onPhotoDelete(); setShowConfirm(false); }} style={{ flex: 1, padding: "12px 0", borderRadius: 11, border: "none", background: "#FF0000", fontSize: 14, fontWeight: 800, color: "white", cursor: "pointer", fontFamily: "inherit" }}>Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Popup — modifier infos van */}
      {showEdit && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500, padding: "0 24px" }}>
          <div style={{ background: "white", borderRadius: 18, padding: "28px 22px", width: "100%", maxWidth: 340, boxShadow: "0 8px 40px rgba(0,0,0,0.25)" }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: C.text, marginBottom: 20 }}>Modifier les infos</div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 1, marginBottom: 6 }}>NOM DU VAN</div>
              <input
                value={editNom}
                onChange={e => setEditNom(e.target.value)}
                style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #D3D1C7", borderRadius: 10, fontSize: 15, fontWeight: 700, fontFamily: "inherit", color: C.text, background: "#F8F6F1", outline: "none", boxSizing: "border-box" }}
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 1, marginBottom: 6 }}>IMMATRICULATION</div>
              <input
                value={editImmat}
                onChange={e => setEditImmat(e.target.value.toUpperCase())}
                style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #D3D1C7", borderRadius: 10, fontSize: 15, fontWeight: 700, fontFamily: "inherit", color: C.text, background: "#F8F6F1", outline: "none", letterSpacing: 2, boxSizing: "border-box" }}
              />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowEdit(false)} style={{ flex: 1, padding: "12px 0", borderRadius: 11, border: "1.5px solid #D3D1C7", background: "white", fontSize: 14, fontWeight: 700, color: C.text, cursor: "pointer", fontFamily: "inherit" }}>Annuler</button>
              <button onClick={() => { onVanUpdate({ nom: editNom, immat: editImmat }); setShowEdit(false); }} style={{ flex: 1, padding: "12px 0", borderRadius: 11, border: "none", background: C.header, fontSize: 14, fontWeight: 800, color: "white", cursor: "pointer", fontFamily: "inherit" }}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Conseils */}
      {showConseils && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
            zIndex: 650,
          }}
          onClick={() => setShowConseils(false)}
        >
          <div
            style={{
              background: "white", borderRadius: "24px 24px 0 0",
              width: "100%", maxWidth: 430, maxHeight: "88vh",
              overflowY: "auto", padding: "24px 20px 32px",
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 18, fontWeight: 900, color: "#2D5A3D", marginBottom: 20 }}>
              {t.conseilsVoyage || "💡 Conseils voyage"}
            </div>

            {conseils.map(c => (
              <div key={c.num} style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#2D5A3D", marginBottom: 5 }}>
                  {c.num}. {c.titre}
                </div>
                <div style={{ fontSize: 13, color: "#1C1C1E", lineHeight: 1.65, whiteSpace: "pre-line" }}>
                  {c.texte}
                </div>
              </div>
            ))}

            <button
              onClick={() => setShowConseils(false)}
              style={{
                width: "100%", padding: "13px 0", marginTop: 8,
                background: "#1A6FBB", color: "white",
                border: "none", borderRadius: 12,
                fontSize: 14, fontWeight: 800,
                cursor: "pointer", fontFamily: "inherit",
              }}
            >
              {t.fermer || "Fermer"}
            </button>
          </div>
        </div>
      )}

      {/* Écran CGU */}
      {showCGU && (
        <div style={{ position: "fixed", inset: 0, zIndex: 700, background: "#F8F6F1", overflowY: "auto" }}>
          <CGUScreen onClose={() => setShowCGU(false)} lang={lang} />
        </div>
      )}

      {/* Modal Info */}
      {showInfo && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
          display: "flex", alignItems: "flex-end", justifyContent: "center",
          zIndex: 600, padding: "0 0 0 0",
        }} onClick={() => setShowInfo(false)}>
          <div style={{
            background: "white", borderRadius: "24px 24px 0 0",
            padding: "28px 22px 36px", width: "100%", maxWidth: 430,
            boxShadow: "0 -8px 40px rgba(0,0,0,0.2)",
          }} onClick={e => e.stopPropagation()}>

            {/* Logo + titre */}
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 16,
                background: C.header, margin: "0 auto 10px",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 26,
              }}>🚐</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: C.text, letterSpacing: 1 }}>CHECKVAN</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{t.carnetBord || "Carnet de bord numérique"}</div>
              <div style={{
                display: "inline-block", marginTop: 6,
                background: "#F0F0EC", borderRadius: 10, padding: "2px 10px",
                fontSize: 11, fontWeight: 700, color: C.muted,
              }}>Version 1.0.0</div>
            </div>

            {/* Email */}
            <div style={{
              background: "#F8F6F1", borderRadius: 12, padding: "10px 14px",
              marginBottom: 16, display: "flex", alignItems: "center", gap: 10,
            }}>
              <span style={{ fontSize: 16 }}>👤</span>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: 1 }}>{t.compteConnecte || "COMPTE CONNECTÉ"}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{userEmail || "—"}</div>
              </div>
            </div>

            {/* Bouton Premium */}
            <button onClick={() => { setShowInfo(false); onShowPremium && onShowPremium(); }} style={{
              width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              background: "linear-gradient(135deg, #2D5A3D, #C4622D)",
              border: "none", borderRadius: 12,
              padding: "13px 14px", marginBottom: 12,
              cursor: "pointer", fontFamily: "inherit",
            }}>
              <span style={{ fontSize: 16 }}>✨</span>
              <span style={{ fontSize: 14, fontWeight: 800, color: "white" }}>{t.voirPlans || "Voir les plans Premium"}</span>
            </button>

            {/* Boutons actions */}
            {[
              { icon: "📱", label: t.installerApp || "Installer l'app sur le téléphone", bg: "#EAF4FF", color: "#1A6FBB", action: () => {} },
              { icon: "🔔", label: t.activerNotifs || "Activer les notifications",       bg: "#FFF8EA", color: "#B87A00", action: () => {} },
              { icon: "🔗", label: t.partagerApp   || "Partager l'app",                  bg: "#F0FAF4", color: C.header,  action: () => {
                if (navigator.share) navigator.share({ title: "CheckVan", url: window.location.href });
              }},
            ].map(({ icon, label, bg, color, action }) => (
              <button key={label} onClick={action} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 12,
                background: bg, border: "none", borderRadius: 12,
                padding: "12px 14px", marginBottom: 8,
                cursor: "pointer", fontFamily: "inherit",
              }}>
                <span style={{ fontSize: 18 }}>{icon}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color }}>{label}</span>
              </button>
            ))}

            {/* Supprimer compte */}
            <button
              onClick={handleDeleteAccount}
              disabled={deletingAccount}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 12,
                background: confirmDeleteAccount ? "#FFDDDD" : "#FFF0F0",
                border: confirmDeleteAccount ? "2px solid #CC0000" : "none",
                borderRadius: 12,
                padding: "12px 14px", marginBottom: 16,
                cursor: deletingAccount ? "not-allowed" : "pointer",
                fontFamily: "inherit",
                opacity: deletingAccount ? 0.6 : 1,
              }}
            >
              <span style={{ fontSize: 18 }}>🗑️</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#CC0000" }}>
                {deletingAccount
                  ? (t.suppressionEnCours || "Suppression...")
                  : confirmDeleteAccount
                    ? (t.confirmerSuppressionCompte || "Confirmer la suppression ?")
                    : (t.supprimerCompte || "Supprimer mon compte")}
              </span>
            </button>

            {/* Langue */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: 1, marginBottom: 8 }}>
                {t.langue || "LANGUE"}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                {[
                  { code: "fr", countryCode: "fr", label: "Français" },
                  { code: "en", countryCode: "gb", label: "English" },
                ].map(l => (
                  <button
                    key={l.code}
                    onClick={() => {
                      localStorage.setItem("checkvan_lang", l.code);
                      setLang?.(l.code);
                    }}
                    style={{
                      flex: 1, padding: 0, height: 64,
                      borderRadius: 10, overflow: "hidden",
                      border: lang === l.code ? `3px solid ${C.header}` : `1.5px solid ${C.muted}30`,
                      cursor: "pointer", fontFamily: "inherit",
                      backgroundImage: `url(https://flagcdn.com/w160/${l.countryCode}.png)`,
                      backgroundSize: "cover", backgroundPosition: "center",
                      position: "relative",
                    }}
                  >
                    <div style={{
                      position: "absolute", inset: 0,
                      background: lang === l.code ? "rgba(0,0,0,0.18)" : "rgba(0,0,0,0.32)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <span style={{
                        fontSize: 12, fontWeight: 800, color: "white",
                        textShadow: "0 1px 4px rgba(0,0,0,0.7)",
                        letterSpacing: 0.5,
                      }}>
                        {l.label}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Contact + CGU */}
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <a href="mailto:contact@checkapp-studio.fr" style={{ fontSize: 12, color: "#1A6FBB", fontWeight: 600, marginBottom: 4, display: "block", textDecoration: "none" }}>
                contact@checkapp-studio.fr
              </a>
              <div
                onClick={() => { setShowInfo(false); setShowCGU(true); }}
                style={{ fontSize: 12, color: C.muted, textDecoration: "underline", cursor: "pointer" }}
              >
                {t.cguConfidentialite || "CGU & Politique de confidentialité"}
              </div>
            </div>

            {/* Fermer */}
            <button onClick={() => setShowInfo(false)} style={{
              width: "100%", padding: "14px 0",
              background: "#1A6FBB", color: "white",
              border: "none", borderRadius: 14,
              fontSize: 15, fontWeight: 800,
              cursor: "pointer", fontFamily: "inherit",
            }}>
              {t.fermer || "Fermer"}
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ background: C.header, padding: "20px 20px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>

          {/* Gauche : titre + date */}
          <div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#F8F6F1" }}>Accueil</div>
            <div style={{ fontSize: 12, color: "rgba(248,246,241,0.65)", marginTop: 2, textTransform: "capitalize" }}>
              {todayFR()}
            </div>
          </div>

          {/* Droite : badge + boutons */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Badge Synchronisé */}
            <div style={{
              display: "flex", alignItems: "center", gap: 5,
              background: "rgba(159,225,203,0.18)", borderRadius: 20,
              padding: "3px 10px 3px 7px",
            }}>
              <span style={{
                width: 7, height: 7, borderRadius: "50%",
                background: "#9FE1CB", display: "block",
                animation: "syncPulse 2s infinite",
              }} />
              <span style={{ fontSize: 11, fontWeight: 800, color: "#9FE1CB", letterSpacing: 0.3 }}>
                {t.synchronise || "Synchronisé"}
              </span>
            </div>

            {/* Badge Conseils */}
            <div
              onClick={() => setShowConseils(true)}
              style={{
                display: "flex", alignItems: "center", gap: 4,
                background: "rgba(255,214,0,0.18)", borderRadius: 20,
                padding: "3px 10px 3px 8px", cursor: "pointer",
              }}
            >
              <span style={{ fontSize: 12 }}>💡</span>
              <span style={{ fontSize: 11, fontWeight: 800, color: "#FFD600", letterSpacing: 0.3 }}>
                {t.conseils || "Conseils"}
              </span>
            </div>

            {/* Bouton ℹ️ */}
            <button onClick={() => setShowInfo(true)} style={{
              width: 30, height: 30, borderRadius: "50%",
              background: "#1A6FBB", border: "none",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", flexShrink: 0,
              fontSize: 15, color: "white", fontWeight: 900,
            }}>ℹ</button>

            {/* Bouton déconnexion */}
            <button onClick={() => logoutUser()} style={{
              width: 30, height: 30, borderRadius: "50%",
              background: "#CC0000", border: "none",
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", flexShrink: 0,
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M16 17l5-5-5-5M21 12H9" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        <style>{`
          @keyframes syncPulse {
            0%   { box-shadow: 0 0 0 0 rgba(159,225,203,0.6); }
            70%  { box-shadow: 0 0 0 6px rgba(159,225,203,0); }
            100% { box-shadow: 0 0 0 0 rgba(159,225,203,0); }
          }
        `}</style>
      </div>

      <div style={{ padding: "16px 16px 0" }}>

        {/* Carte van */}
        <div style={{
          background: "white", borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 2px 16px rgba(44,90,61,0.09)",
          marginBottom: 16,
        }}>
          {/* Zone photo — style CHECKAR */}
          <div style={{ position: "relative", height: 190, overflow: "hidden" }}>

            {/* Fond : photo ou placeholder */}
            {van?.photo ? (
              <div style={{ position: "absolute", inset: 0, background: `url(${van.photo}) center/cover no-repeat` }} />
            ) : (
              <div style={{ position: "absolute", inset: 0, background: "#EAF3DE", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <VanSVG />
              </div>
            )}

            {/* Gradient bas */}
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(0,0,0,0.28) 0%, transparent 35%, rgba(0,0,0,0.65) 100%)" }} />

            {/* Icônes haut-gauche : 🗑️ (si photo) + ✏️ */}
            <div style={{ position: "absolute", top: 10, left: 10, display: "flex", gap: 6 }}>
              {van?.photo && (
                <FadeBtn
                  onClick={() => setShowConfirm(true)}
                  style={{ background: "rgba(0,0,0,0.40)", backdropFilter: "blur(4px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, cursor: "pointer", fontFamily: "inherit" }}
                >
                  🗑️
                </FadeBtn>
              )}
              <FadeBtn
                onClick={openEdit}
                style={{ background: "rgba(0,0,0,0.40)", backdropFilter: "blur(4px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, cursor: "pointer", fontFamily: "inherit" }}
              >
                ✏️
              </FadeBtn>
            </div>

            {/* Bas : nom/immat à gauche — bouton Photo à droite */}
            <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 900, color: "white", lineHeight: 1.1 }}>
                  {van?.nom || "Mon Van"}
                </div>
                {van?.immat && (
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.80)", fontWeight: 700, letterSpacing: 1.5, marginTop: 3 }}>
                    {van.immat}
                  </div>
                )}
              </div>
              <FadeBtn
                onClick={() => fileRef.current?.click()}
                style={{ background: "rgba(255,255,255,0.20)", backdropFilter: "blur(4px)", border: "1.5px solid rgba(255,255,255,0.40)", borderRadius: 9, padding: "7px 16px", color: "white", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
              >
                Photo
              </FadeBtn>
            </div>

            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
          </div>

          {/* Score + infos */}
          <div style={{ padding: "14px 16px 16px", display: "flex", alignItems: "center", gap: 16 }}>
            <ScoreRing score={score} />
            <div style={{ flex: 1 }}>
              <div style={{ background: "#F8F6F1", borderRadius: 10, padding: "10px 14px", marginBottom: 10, textAlign: "center" }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 5, lineHeight: 1 }}>
                  <span style={{ fontSize: 24, fontWeight: 900, color: C.header }}>
                    {(() => {
                      const kms = (depenses || [])
                        .filter(d => d.type === "carburant")
                        .map(d => Number(d.km))
                        .filter(k => !isNaN(k) && k > 0);
                      return kms.length ? Number(Math.max(...kms)).toLocaleString("fr-FR") : "—";
                    })()}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 0.5 }}>km</span>
                  <span style={{ fontSize: 9, fontWeight: 700, color: C.muted, letterSpacing: 1 }}>COMPTEUR</span>
                </div>
              </div>
              {(() => {
                const urgent = rappels.filter(r => r.color === "#C4622D" || r.color === "#EA7B20").length;
                return (
                  <div style={{
                    display: "inline-flex", alignItems: "center",
                    background: urgent === 0 ? "#EAF3DE" : "#FEF3E8",
                    borderRadius: 20, padding: "4px 12px",
                  }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700,
                      color: urgent === 0 ? "#27500A" : "#B45309",
                    }}>
                      {urgent === 0 ? (t.toutAJour || "✓ Tout est à jour") : `${urgent} ${t.echeances || "échéance(s)"}`}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Rappels + Météo côte à côte */}
        <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>

          {/* Fenêtre Rappels */}
          <div style={{
            flex: 1, background: "white", borderRadius: 14,
            padding: "12px 12px", boxShadow: "0 1px 8px rgba(44,90,61,0.07)",
          }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, letterSpacing: 1, marginBottom: 10, textAlign: "center" }}>
              {t.rappels || "RAPPELS"}
            </div>
            {[
              { icon: "🛡️", label: t.assurance || "Assurance", r: rappels[0] },
              { icon: "🔧", label: t.revision   || "Révision",  r: rappels[1] },
              { icon: "🚗", label: t.ct         || "CT",        r: rappels[2] },
            ].map(({ icon, label, r }) => (
              <div key={label} style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                marginBottom: 7,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <span style={{ fontSize: 13 }}>{icon}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{label}</span>
                </div>
                <span style={{ fontSize: 11, fontWeight: 800, color: r?.color || C.muted }}>
                  {r?.text || "—"}
                </span>
              </div>
            ))}
          </div>

          {/* Fenêtre Météo */}
          <div style={{
            flex: 1, background: "white", borderRadius: 14,
            padding: "12px 12px", boxShadow: "0 1px 8px rgba(44,90,61,0.07)",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            textAlign: "center",
          }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, letterSpacing: 1, marginBottom: 10, textAlign: "center" }}>
              {t.meteo || "MÉTÉO"}
            </div>
            {weatherErr === "geo" ? (
              <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.4 }}>
                📍 Activez la localisation
              </div>
            ) : weatherErr === "api" ? (
              <div style={{ fontSize: 11, color: C.muted }}>Météo indisponible</div>
            ) : !weather ? (
              <div style={{ fontSize: 11, color: C.muted }}>Chargement…</div>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 26, fontWeight: 900, color: C.text, lineHeight: 1 }}>
                    {weather.temp}°
                  </span>
                  <span style={{ fontSize: 22 }}>{weather.emoji}</span>
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 4 }}>
                  {weather.city}
                </div>
                <div style={{ fontSize: 11, color: C.muted }}>
                  {weather.desc}
                </div>
                {weather.usingDefault && (
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 4, opacity: 0.7 }}>
                    📍 Activez la localisation pour votre ville
                  </div>
                )}
              </>
            )}
          </div>

        </div>

        {/* Checklist + Autonomie côte à côte */}
        {(() => {
          const checkVals = Object.values(checks || {});
          const nOk   = checkVals.filter(v => v === "ok").length;
          const nWarn = checkVals.filter(v => v === "warn").length;
          const nNok  = checkVals.filter(v => v === "nok").length;
          const total = nOk + nWarn + nNok;
          const autoVals = autonomie || { eau: 70, gaz: 60, batterie: 80, eauxUsees: 20 };
          const conseil  = getConseilAuto(autoVals, t);
          return (
            <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>

              {/* Carré Checklist */}
              <button
                onClick={() => setTab("checklist")}
                style={{
                  flex: 1, background: "white", borderRadius: 14,
                  padding: "12px 12px", boxShadow: "0 1px 8px rgba(44,90,61,0.07)",
                  border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
                  display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "stretch",
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, letterSpacing: 1, marginBottom: 10, textAlign: "center" }}>
                  {t.checklist_label || "CHECKLIST"}
                </div>
                {total === 0 ? (
                  <div style={{ fontSize: 11, color: C.muted }}>Aucune vérif.</div>
                ) : (
                  <div>
                    {nOk > 0 && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>✅ OK</span>
                        <span style={{ fontSize: 12, fontWeight: 800, color: "#2D5A3D" }}>{nOk}</span>
                      </div>
                    )}
                    {nWarn > 0 && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>⚠️ Attention</span>
                        <span style={{ fontSize: 12, fontWeight: 800, color: "#A85E00" }}>{nWarn}</span>
                      </div>
                    )}
                    {nNok > 0 && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>❌ Problème</span>
                        <span style={{ fontSize: 12, fontWeight: 800, color: "#C4622D" }}>{nNok}</span>
                      </div>
                    )}
                  </div>
                )}
              </button>

              {/* Carré Autonomie */}
              <div style={{
                flex: 1, background: "white", borderRadius: 14,
                padding: "12px 12px", boxShadow: "0 1px 8px rgba(44,90,61,0.07)",
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, letterSpacing: 1, marginBottom: 10, textAlign: "center" }}>
                  {t.autonomie_label || "AUTONOMIE"}
                </div>
                {RESOURCES.map(r => {
                  const val = autoVals[r.id] ?? 0;
                  return (
                    <div key={r.id} style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      marginBottom: 5,
                    }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{r.label}</span>
                      <span style={{ fontSize: 12, fontWeight: 800, color: r.color }}>
                        {val}<span style={{ fontSize: 10, fontWeight: 600 }}>{r.unit}</span>
                      </span>
                    </div>
                  );
                })}
                <div style={{ fontSize: 10, color: conseil.color, fontWeight: 600, marginTop: 6, lineHeight: 1.3 }}>
                  {conseil.text}
                </div>
              </div>

            </div>
          );
        })()}

        {/* Barre SOS */}
        <button
          onClick={onSOS}
          style={{
            width: "100%", height: "120px",
            borderRadius: "16px", border: "3px solid white",
            background: "#FF0000",
            color: "white",
            cursor: "pointer", fontFamily: "inherit",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 4,
            padding: 0,
          }}
        >
          <span style={{ fontSize: 36, lineHeight: 1 }}>🆘</span>
          <span style={{ fontSize: 16, fontWeight: 900, letterSpacing: 1 }}>{t.boutonSecours || "BOUTON DE SECOURS"}</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.85)" }}>
            {t.accidentAssistance || "Accident · Assistance · Constat · Services proches"}
          </span>
        </button>
      </div>
    </div>
  );
}
